// src/components/settings/ProfileSettings.jsx 
import React, { useState, useEffect } from 'react';
import { 
  User, 
  Mail, 
  Phone, 
  MapPin, 
  School, 
  BookOpen,
  Save,
  AlertCircle,
  Briefcase,
  Building,
  Edit,
  Navigation
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { 
  getUniqueProvinces, 
  getDistrictsByProvince, 
  getSchoolPhases,
  getSchoolsByFilters,
  formatSchoolPhase,
  toPhasePED
} from '../../datalists/Schools';

// Subjects and Grades data (same as before)
const SUBJECTS = {
  PRIMARY: [
    "English Home Language", "English First Additional Language", "Life Orientation",
    "Creative Arts", "Mathematics", "Natural Sciences", "Social Sciences",
    "Economic Management Sciences", "Technology"
  ],
  SECONDARY: [
    "English Home Language", "English First Additional Language", "Life Orientation", 
    "Accounting", "Agricultural Sciences", "Business Studies", "Civil Technology",
    "Computer Applications Technology", "Consumer Studies", "Economics", "Geography",
    "History", "Hospitality Studies", "Information Technology", "Life Sciences",
    "Mathematics", "Mathematical Literacy", "Technical Mathematics", 
    "Physical Sciences", "Technical Sciences", "Tourism", "Creative Arts",
    "Natural Sciences", "Social Sciences", "Economic Management Sciences", "Technology"
  ]
};

const GRADES = {
  PRIMARY: ["R", "1", "2", "3", "4", "5", "6", "7"],
  SECONDARY: ["8", "9", "10", "11", "12"],
  COMBINED: ["R", "1", "2", "3", "4", "5", "6", "7", "8", "9", "10", "11", "12"]
};

const PROJECT_ROLES = ["Tutor", "Coordinator", "Director"];

const ProfileSettings = ({ onUnsavedChanges }) => {
  const { currentUser, userProfile, updateProfile } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  // Available options
  const [provinces] = useState(getUniqueProvinces());
  const [districts, setDistricts] = useState([]);
  const [schools, setSchools] = useState([]);
  const [availableSubjects, setAvailableSubjects] = useState([]);
  const [availableGrades, setAvailableGrades] = useState([]);

  const [formData, setFormData] = useState({
    title: '',
    firstName: '',
    lastName: '',
    email: '',
    mobileNumber: '',
    institution: '',
    position: '',
    schoolName: '',
    schoolPhase: '',
    grades: [],
    subjects: [],
    province: '',
    district: '',
    circuit: '',
    projectName: '',
    shareWith: []
  });

  // Initialize form data from user profile
  useEffect(() => {
    if (userProfile) {
      setFormData({
        title: userProfile.title || '',
        firstName: userProfile.firstName || '',
        lastName: userProfile.lastName || '',
        email: userProfile.email || currentUser?.email || '',
        mobileNumber: userProfile.mobileNumber || '',
        institution: userProfile.institution || '',
        position: userProfile.position || '',
        schoolName: userProfile.schoolName || '',
        schoolPhase: userProfile.schoolPhase || '',
        grades: userProfile.grades || [],
        subjects: userProfile.subjects || [],
        province: userProfile.province || '',
        district: userProfile.district || '',
        circuit: userProfile.circuit || '',
        projectName: userProfile.projectName || '',
        shareWith: userProfile.shareWith || []
      });
    }
  }, [userProfile, currentUser]);

  // Handle form changes
  useEffect(() => {
    if (userProfile && JSON.stringify(formData) !== JSON.stringify({
      title: userProfile.title || '',
      firstName: userProfile.firstName || '',
      lastName: userProfile.lastName || '',
      email: userProfile.email || currentUser?.email || '',
      mobileNumber: userProfile.mobileNumber || '',
      institution: userProfile.institution || '',
      position: userProfile.position || '',
      schoolName: userProfile.schoolName || '',
      schoolPhase: userProfile.schoolPhase || '',
      grades: userProfile.grades || [],
      subjects: userProfile.subjects || [],
      province: userProfile.province || '',
      district: userProfile.district || '',
      circuit: userProfile.circuit || '',
      projectName: userProfile.projectName || '',
      shareWith: userProfile.shareWith || []
    })) {
      onUnsavedChanges(true);
    } else {
      onUnsavedChanges(false);
    }
  }, [formData, userProfile, onUnsavedChanges, currentUser]);

  // Update districts when province changes
  useEffect(() => {
    if (formData.province) {
      const provinceDistricts = getDistrictsByProvince(formData.province);
      setDistricts(provinceDistricts);
    } else {
      setDistricts([]);
    }
  }, [formData.province]);

  // Update schools when district or phase changes
  useEffect(() => {
    if (formData.province && formData.district && formData.institution === 'School') {
      let phasePED = null;
      if (formData.schoolPhase) {
        phasePED = toPhasePED(formData.schoolPhase);
      }
      
      const filteredSchools = getSchoolsByFilters(
        phasePED,
        formData.province,
        formData.district
      );
      setSchools(filteredSchools);
    } else {
      setSchools([]);
    }
  }, [formData.province, formData.district, formData.schoolPhase, formData.institution]);

  // Update available subjects and grades based on school phase
  useEffect(() => {
    if (formData.schoolPhase) {
      if (formData.schoolPhase === 'Primary') {
        setAvailableSubjects(SUBJECTS.PRIMARY);
        setAvailableGrades(GRADES.PRIMARY);
      } else if (formData.schoolPhase === 'Secondary') {
        setAvailableSubjects(SUBJECTS.SECONDARY);
        setAvailableGrades(GRADES.SECONDARY);
      } else if (formData.schoolPhase === 'Combined') {
        setAvailableSubjects(SUBJECTS.SECONDARY);
        setAvailableGrades(GRADES.COMBINED);
      }
    }
  }, [formData.schoolPhase]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleMultiSelect = (name, value) => {
    setFormData(prev => ({
      ...prev,
      [name]: prev[name].includes(value) 
        ? prev[name].filter(item => item !== value)
        : [...prev[name], value]
    }));
  };

  const handleSave = async () => {
    setLoading(true);
    setError('');
    setSuccess(false);

    try {
      await updateProfile(formData);
      setSuccess(true);
      setIsEditing(false);
      setTimeout(() => setSuccess(false), 3000);
    } catch (error) {
      setError(error.message || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (userProfile) {
      setFormData({
        title: userProfile.title || '',
        firstName: userProfile.firstName || '',
        lastName: userProfile.lastName || '',
        email: userProfile.email || currentUser?.email || '',
        mobileNumber: userProfile.mobileNumber || '',
        institution: userProfile.institution || '',
        position: userProfile.position || '',
        schoolName: userProfile.schoolName || '',
        schoolPhase: userProfile.schoolPhase || '',
        grades: userProfile.grades || [],
        subjects: userProfile.subjects || [],
        province: userProfile.province || '',
        district: userProfile.district || '',
        circuit: userProfile.circuit || '',
        projectName: userProfile.projectName || '',
        shareWith: userProfile.shareWith || []
      });
    }
    setIsEditing(false);
    setError('');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Profile Settings</h2>
          <p className="text-sm text-gray-600">Manage your personal information and account details</p>
        </div>
        <div className="flex items-center gap-2">
          {!isEditing ? (
            <button
              onClick={() => setIsEditing(true)}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm"
            >
              <Edit className="w-4 h-4" />
              Edit Profile
            </button>
          ) : (
            <div className="flex items-center gap-2">
              <button
                onClick={handleCancel}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 text-sm"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={loading}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 text-sm"
              >
                {loading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    Save Changes
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Status Messages */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-md flex items-center gap-2">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
          <span className="text-red-700 text-sm">{error}</span>
        </div>
      )}

      {success && (
        <div className="p-4 bg-green-50 border border-green-200 rounded-md">
          <p className="text-green-700 text-sm">Profile updated successfully!</p>
        </div>
      )}

      {/* Profile Form */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="space-y-6">
          {/* Personal Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
              <User className="w-5 h-5" />
              Personal Information
            </h3>
            
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                <select
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  disabled={!isEditing}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm disabled:bg-gray-50 disabled:text-gray-500"
                >
                  <option value="">Select title</option>
                  <option value="Mr">Mr</option>
                  <option value="Mrs">Mrs</option>
                  <option value="Ms">Ms</option>
                  <option value="Dr">Dr</option>
                  <option value="Prof">Prof</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">First Name *</label>
                <input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  disabled={!isEditing}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm disabled:bg-gray-50 disabled:text-gray-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Last Name *</label>
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  disabled={!isEditing}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm disabled:bg-gray-50 disabled:text-gray-500"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    disabled
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm bg-gray-50 text-gray-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Mobile Number *</label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="tel"
                    name="mobileNumber"
                    value={formData.mobileNumber}
                    onChange={handleChange}
                    disabled={!isEditing}
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm disabled:bg-gray-50 disabled:text-gray-500"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Institution Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
              {formData.institution === 'School' && <School className="w-5 h-5" />}
              {formData.institution === 'Project' && <Briefcase className="w-5 h-5" />}
              {(formData.institution === 'District' || formData.institution === 'Province') && <Building className="w-5 h-5" />}
              Institution Information
            </h3>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Institution Type *</label>
                <select
                  name="institution"
                  value={formData.institution}
                  onChange={handleChange}
                  disabled={!isEditing}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm disabled:bg-gray-50 disabled:text-gray-500"
                >
                  <option value="">Select institution</option>
                  <option value="School">School</option>
                  <option value="District">District</option>
                  <option value="Province">Province</option>
                  <option value="Project">Project</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Position *</label>
                <select
                  name="position"
                  value={formData.position}
                  onChange={handleChange}
                  disabled={!isEditing}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm disabled:bg-gray-50 disabled:text-gray-500"
                >
                  <option value="">Select position</option>
                  {formData.institution === 'School' && (
                    <>
                      <option value="Teacher">Teacher</option>
                      <option value="Head of Department">Head of Department</option>
                      <option value="Deputy Principal">Deputy Principal</option>
                      <option value="Principal">Principal</option>
                    </>
                  )}
                  {formData.institution === 'District' && (
                    <>
                      <option value="Subject Advisor">Subject Advisor</option>
                      <option value="District Director">District Director</option>
                      <option value="Deputy District Director">Deputy District Director</option>
                      <option value="Chief Education Specialist">Chief Education Specialist</option>
                    </>
                  )}
                  {formData.institution === 'Province' && (
                    <>
                      <option value="Chief Education Specialist">Chief Education Specialist</option>
                      <option value="Deputy Chief Education Specialist">Deputy Chief Education Specialist</option>
                      <option value="Provincial Director">Provincial Director</option>
                    </>
                  )}
                  {formData.institution === 'Project' && (
                    <>
                      {PROJECT_ROLES.map(role => (
                        <option key={role} value={role}>{role}</option>
                      ))}
                    </>
                  )}
                  {formData.institution && (
                    <option value="Official">Official</option>
                  )}
                </select>
              </div>
            </div>

            {/* Location Information */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Province *</label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <select
                    name="province"
                    value={formData.province}
                    onChange={handleChange}
                    disabled={!isEditing}
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm disabled:bg-gray-50 disabled:text-gray-500"
                  >
                    <option value="">Select province</option>
                    {provinces.map(province => (
                      <option key={province} value={province}>{province}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">District *</label>
                <select
                  name="district"
                  value={formData.district}
                  onChange={handleChange}
                  disabled={!isEditing || !formData.province}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm disabled:bg-gray-50 disabled:text-gray-500"
                >
                  <option value="">Select district</option>
                  {districts.map(district => (
                    <option key={district} value={district}>{district}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Circuit</label>
                <div className="relative">
                  <Navigation className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    name="circuit"
                    value={formData.circuit}
                    onChange={handleChange}
                    disabled={!isEditing}
                    placeholder="Enter circuit name"
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm disabled:bg-gray-50 disabled:text-gray-500"
                  />
                </div>
              </div>
            </div>

            {/* School-specific fields - rest remains the same */}
            {formData.institution === 'School' && (
              <div className="space-y-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <h5 className="font-medium text-blue-900 flex items-center gap-2">
                  <School className="w-4 h-4" />
                  School Details
                </h5>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">School Phase *</label>
                    <select
                      name="schoolPhase"
                      value={formData.schoolPhase}
                      onChange={handleChange}
                      disabled={!isEditing}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm disabled:bg-gray-50 disabled:text-gray-500"
                    >
                      <option value="">Select phase</option>
                      {getSchoolPhases().map(phase => (
                        <option key={phase} value={formatSchoolPhase(phase)}>
                          {formatSchoolPhase(phase)}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">School Name *</label>
                    <select
                      name="schoolName"
                      value={formData.schoolName}
                      onChange={handleChange}
                      disabled={!isEditing || !formData.province || !formData.district || !formData.schoolPhase}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm disabled:bg-gray-50 disabled:text-gray-500"
                    >
                      <option value="">Select school</option>
                      {schools.map(school => (
                        <option key={school} value={school}>{school}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Grades and Subjects selection - same as before with disabled states */}
                {formData.schoolPhase && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Grades Taught *
                    </label>
                    <div className="grid grid-cols-4 sm:grid-cols-6 lg:grid-cols-8 gap-2 max-h-40 overflow-y-auto border border-gray-200 rounded-md p-3 bg-white">
                      {availableGrades.map(grade => (
                        <label key={grade} className="flex items-center space-x-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={formData.grades.includes(grade)}
                            onChange={() => handleMultiSelect('grades', grade)}
                            disabled={!isEditing}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:opacity-50"
                          />
                          <span className="text-sm text-gray-700">Grade {grade}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                {formData.schoolPhase && formData.schoolPhase !== 'Primary' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Subjects Taught *
                    </label>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 max-h-60 overflow-y-auto border border-gray-200 rounded-md p-3 bg-white">
                      {availableSubjects.map(subject => (
                        <label key={subject} className="flex items-center space-x-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={formData.subjects.includes(subject)}
                            onChange={() => handleMultiSelect('subjects', subject)}
                            disabled={!isEditing}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:opacity-50"
                          />
                          <span className="text-sm text-gray-700">{subject}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Project-specific fields */}
            {formData.institution === 'Project' && (
              <div className="space-y-4 p-4 bg-green-50 rounded-lg border border-green-200">
                <h5 className="font-medium text-green-900 flex items-center gap-2">
                  <Briefcase className="w-4 h-4" />
                  Project Details
                </h5>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Project Name *</label>
                  <input
                    type="text"
                    name="projectName"
                    value={formData.projectName}
                    onChange={handleChange}
                    disabled={!isEditing}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm disabled:bg-gray-50 disabled:text-gray-500"
                    placeholder="Enter project name"
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileSettings;